import { API_BASE_URL } from "../urls.api";
import { Resource } from "@/pages/rooms/details/components/resources/types";

export const createResource = async (
  body: Omit<Resource, "_id">
): Promise<{ data: Resource | null; error?: string }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/resources/room/${body.linkId}`, {
      method: "post",
      credentials: "include",
      body: JSON.stringify({ ...body }),
      headers: { "Content-Type": "application/json" },
    });
    const { data } = await response.json();
    return { data };
  } catch (error) {
    return { data: null, error: error as string };
  }
};

export const createResourceToNode = async (
  body: Omit<Resource, "_id">
): Promise<{ data: Resource | null; error?: string }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/resources`, {
      method: "post",
      credentials: "include",
      body: JSON.stringify(body),
      headers: { "Content-Type": "application/json" },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        data: null,
        error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
      };
    }

    const { data, error } = await response.json();

    if (error) {
      return { data: null, error };
    }

    return { data };
  } catch (error) {
    return {
      data: null,
      error: error instanceof Error ? error.message : "Error de conexión",
    };
  }
};

export const updateResource = async (
  resource: Partial<Resource>,
  resourceId: string
): Promise<{ data: Resource | null; error: string }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/resources/${resourceId}/content`, {
      method: "PATCH",
      credentials: "include",
      body: JSON.stringify(resource),
      headers: { "Content-Type": "application/json" },
    });

    return response.json();
  } catch (error) {
    return { data: null, error: error };
  }
};

export const deleteResource = async (
  ownerId: string,
  resourceId: string
): Promise<{ data: boolean | null; error?: string }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/resources/${resourceId}/owner/${ownerId}`, {
      method: "DELETE",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        data: null,
        error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
      };
    }

    return { data: true };
  } catch (error) {
    return {
      data: null,
      error: error instanceof Error ? error.message : "Error de conexión",
    };
  }
};

export const getResourcesByRoomAndModule = async (
  roomId: string,
  ownerId: string,
  module?: string
): Promise<{ data: Resource[] }> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/resources/owner/${ownerId}/room/${roomId}${module ? `?module=${module}` : ""}`,
      {
        ...(module && { search: `?module=${module}` }),
        method: "get",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      }
    );
    const data = await response.json();
    return data;
  } catch (error) {
    return { data: [] };
  }
};

export const getProtocolsByNode = async (
  nodeId: string,
  ownerId: string,
  module?: string
): Promise<{ data: Resource[] }> => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/resources/owner/${ownerId}/node/${nodeId}${module ? `?moduleType=${module}` : ""}`,
      {
        ...(module && { search: `?moduleType=${module}` }),
        method: "get",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      }
    );
    const data = await response.json();
    return data;
  } catch (error) {
    return { data: [] };
  }
};

export const getGraph = async (ownerId: string, roomId: string) => {
  const response = await fetch(`${API_BASE_URL}/nodes/path/${ownerId}/room/${roomId}`, {
    method: "get",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
  });
  const { data } = await response.json();

  return data;
};

export const getProtocolsByNodes = async (
  roomId: string,
  ownerId: string,
  module?: string
): Promise<{ data: Resource[] }> => {
  try {
    const nodeGraph = await getGraph(ownerId, roomId);

    const protocolsPromises = nodeGraph.map((node: { _id: string }) =>
      getProtocolsByNode(node._id, ownerId, module ?? "")
    );
    const protocolsData = await Promise.all(protocolsPromises);
    const protocols = protocolsData.flatMap((protocolResponse) => protocolResponse.data);

    return {
      data: protocols.map((protocol) => ({
        ...protocol,
        nodeName:
          nodeGraph.find((node: { _id: string }) => node._id === protocol.linkId)?.content?.name ||
          "",
      })),
    };
  } catch (error) {
    return { data: [] };
  }
};

export const getResourcesWithInheritance = async (
  nodeIds: string[],
  ownerId: string,
  nodeInfo: Array<{ id: string; name: string; isCurrent: boolean }>,
  module?: string
): Promise<{ data: Resource[] }> => {
  try {
    // Hacer llamadas paralelas para cada nodo
    const promises = nodeIds.map((nodeId) => getProtocolsByNode(nodeId, ownerId, module));

    const results = await Promise.all(promises);

    // Combinar y marcar recursos con información de herencia
    const combinedResources: Resource[] = [];

    results.forEach((response, index) => {
      const currentNodeId = nodeIds[index];
      const currentNodeInfo = nodeInfo.find((n) => n.id === currentNodeId);

      if (!currentNodeInfo) {
        return;
      }

      response.data.forEach((resource) => {
        // Corregir la lógica: isProtocolMode debe ser true para recursos heredados (ancestros)
        const isProtocolMode = !currentNodeInfo.isCurrent;

        combinedResources.push({
          ...resource,
          isProtocolMode: isProtocolMode, // true para ancestros (heredados), false para actual
          nodeId: currentNodeId,
          nodeName: currentNodeInfo.name, // nombre real del nodo
          inheritanceLevel: currentNodeInfo.isCurrent
            ? 0
            : nodeInfo.length - 1 - nodeInfo.findIndex((n) => n.id === currentNodeId), // nivel de herencia: 0=actual, 1=padre, 2=abuelo, etc.
        });
      });
    });

    return { data: combinedResources };
  } catch (error) {
    return { data: [] };
  }
};

export const moveResourceToNode = async (
  resourceId: string,
  nodeId: string
): Promise<{ data: Resource & { nodeId: string } }> => {
  const response = await fetch(`${API_BASE_URL}/resources/${resourceId}/link/${nodeId}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
  });
  const { data } = await response.json();
  return {
    data: {
      ...data,
      nodeId,
    },
  };
};

export const deleteAllResourcesByNode = async (
  nodeId: string,
  ownerId: string
): Promise<Response> =>
  fetch(`${API_BASE_URL}/resources/owner/${ownerId}/nodeId/${nodeId}`, {
    method: "DELETE",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
  }).then((response) => response.json());

export const filesAdmin = async (
  ownerId: string,
  resourceId: string,
  field: string,
  files: Array<{ filename: string; file?: File }>
) => {
  const formData = new FormData();
  formData.append("field", field);

  const filenames = files.filter((f) => !f.file).map((f) => f.filename);
  formData.append("filenames", JSON.stringify(filenames));

  files.forEach((fileData) => {
    if (fileData.file) {
      formData.append("files", fileData.file);
    }
  });

  const response = await fetch(`${API_BASE_URL}/resources/${resourceId}/owner/${ownerId}/upload`, {
    method: "POST",
    credentials: "include",
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const error = new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    (error as any).statusCode = response.status;
    (error as any).response = response;
    throw error;
  }

  const { data } = await response.json();
  return data;
};

export const getFileUrl = async (fileName: string): Promise<string> => {
  const response = await fetch(`${API_BASE_URL}/resources/file?fileName=${fileName}`, {
    method: "GET",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
  });
  const data = await response.json();
  return data.data;
};

export const getResourceCountsByNodeIds = async (
  ids: string[]
): Promise<{ data: Record<string, number>; error?: string }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/resources/nodes/counter`, {
      method: "POST",
      credentials: "include",
      body: JSON.stringify({ ids }),
      headers: { "Content-Type": "application/json" },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    return {
      data: {},
      error: error instanceof Error ? error.message : "Error de conexión",
    };
  }
};
